import { useLocation, useNavigate, Link } from "react-router";
import {
  ChevronRight,
  LogOut,
  User,
  Settings,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import { GlobalSearchBar } from "@/features/search/GlobalSearchBar";
import { AlertsPanel } from "@/features/alerts/AlertsPanel";
const breadcrumbMap: Record<string, string> = {
  dashboard: "Dashboard",
  clients: "Clients",
  cases: "Case Management",
  shelter: "Shelter",
  legal: "Legal / Court",
  "sexual-assault": "Sexual Assault",
  reports: "Reports",
  volunteers: "Volunteers",
  events: "Events",
  admin: "Admin",
  settings: "Settings",
  users: "Users",
  agencies: "Agencies",
  config: "Configuration",
  "waiting-list": "Waiting List",
};

export function Header() {
  const { user, logout } = useAuth();
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const currentAgency = useAppStore((s) => s.currentAgency);
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const navigate = useNavigate();
  const location = useLocation();

  const segments = location.pathname.split("/").filter(Boolean);
  const breadcrumbs = segments.map((seg, idx) => ({
    label: breadcrumbMap[seg] ?? seg,
    href: "/" + segments.slice(0, idx + 1).join("/"),
    isLast: idx === segments.length - 1,
  }));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
    : "?";

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card/80 backdrop-blur-md px-5">
      {/* Left: menu toggle + breadcrumbs */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Toggle sidebar"
        >
          {sidebarCollapsed ? (
            <PanelLeft className="h-5 w-5" />
          ) : (
            <PanelLeftClose className="h-5 w-5" />
          )}
        </button>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav className="hidden items-center gap-1.5 text-sm sm:flex" aria-label="Breadcrumb">
            {currentAgency && (
              <>
                <span className="font-medium text-muted-foreground/70 max-w-32 truncate">
                  {currentAgency.name}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-border" />
              </>
            )}
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.href} className="flex items-center gap-1.5">
                {i > 0 && (
                  <ChevronRight className="h-3.5 w-3.5 text-border" />
                )}
                {crumb.isLast ? (
                  <span className="font-semibold text-foreground">
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.href}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        )}
      </div>

      {/* Right: search + notifications + user */}
      <div className="flex items-center gap-1.5">
        {/* Global search */}
        <GlobalSearchBar />

        {/* Theme toggle */}
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <Sun className="h-[18px] w-[18px]" />
          ) : (
            <Moon className="h-[18px] w-[18px]" />
          )}
        </button>

        {/* Notifications / Alerts */}
        <AlertsPanel />

        {/* Divider */}
        <div className="mx-1.5 h-6 w-px bg-border" />

        {/* User dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-accent">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-semibold text-white shadow-sm">
                {initials}
              </div>
              <div className="hidden text-left md:block">
                <p className="text-sm font-medium leading-none">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {user?.email?.split("@")[0]}
                </p>
              </div>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 min-w-[200px] rounded-xl border bg-card p-1.5 shadow-lg shadow-black/10"
              align="end"
              sideOffset={8}
            >
              <DropdownMenu.Label className="px-2.5 py-2 text-xs font-medium text-muted-foreground">
                {user?.email}
              </DropdownMenu.Label>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors hover:bg-accent"
                onSelect={() => navigate("/settings")}
              >
                <User className="h-4 w-4 text-muted-foreground" />
                Profile
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors hover:bg-accent"
                onSelect={() => navigate("/settings")}
              >
                <Settings className="h-4 w-4 text-muted-foreground" />
                Settings
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-destructive outline-none transition-colors hover:bg-destructive/10"
                onSelect={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
